/**
 * useAlgorandWallet Hook
 *
 * Custom React hook for managing Algorand wallet data and state
 */

import { useQuery } from '@tanstack/react-query';
import { algorandAPI, microAlgosToAlgo } from '../services/algorandApi';
import type { AlgorandWalletData } from '../types/algorand.types';

/**
 * Fetch ALGO price
 */
export const useAlgoPrice = () => {
  return useQuery({
    queryKey: ['algorand', 'price'],
    queryFn: () => algorandAPI.getAlgoPrice(),
    staleTime: 60_000, // 1 minute
    refetchInterval: 2 * 60_000, // Refetch every 2 minutes
  });
};

/**
 * Fetch wallet data for multiple addresses
 */
export const useAlgorandWallets = (addresses: string[]) => {
  const { data: algoPrice = 0 } = useAlgoPrice();

  const {
    data: wallets,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['algorand', 'wallets', addresses, algoPrice],
    queryFn: () => algorandAPI.getMultipleWalletsData(addresses, algoPrice),
    enabled: addresses.length > 0 && algoPrice > 0,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });

  // Calculate aggregated metrics
  const totalBalance = wallets?.reduce((sum, w) => sum + w.balance, 0) || 0;
  const totalBalanceUsd = wallets?.reduce((sum, w) => sum + w.balanceUsd, 0) || 0;
  const totalPendingRewards = wallets?.reduce((sum, w) => sum + w.pendingRewards, 0) || 0;
  const totalPendingRewardsUsd = wallets?.reduce((sum, w) => sum + w.pendingRewardsUsd, 0) || 0;
  const totalRewards = wallets?.reduce((sum, w) => sum + w.totalRewards, 0) || 0;

  // Count unique assets
  const uniqueAssets = new Set<number>();
  wallets?.forEach(w => w.assets.forEach(a => uniqueAssets.add(a.assetId)));

  // Count participating accounts
  const participatingCount = wallets?.filter(w => w.isParticipating).length || 0;

  return {
    wallets: wallets || [],
    isLoading,
    error,
    refetch,
    algoPrice,
    // Aggregated metrics
    totalBalance,
    totalBalanceUsd,
    totalPendingRewards,
    totalPendingRewardsUsd,
    totalRewards,
    totalRewardsUsd: totalRewards * algoPrice,
    totalAssets: uniqueAssets.size,
    participatingCount,
  };
};
